import { ClientActionCacheService } from "@cervval.js/client-action/src/services/client.action.cache.service";
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { ClientRouteOptions, TypedClientAjaxProxy } from "@cervval.js/client-html5-spa/routes";
import { CatchErrorWithDialog } from "@cervval.js/client-html5-ui/src/tools/catchErrorWithDialog.decorator";
import { CLIENT_WEB_SOCKET_SERVICE, ClientWebSocketService } from "@cervval.js/client-websocket/src/clientWebSocket.service";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { setProp } from "@cervval.js/common/src/tools/any.tools";
import { addToSetMap } from "@cervval.js/common/src/tools/collection.map.tools";
import { CommonEventInit } from "@cervval.js/common/src/tools/event.tools";
import { objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { DataRoutes } from "@ehealth_tools/common-security/src/routes/data.routes";
import { SyncWSRoutes } from "@ehealth_tools/common-security/src/routes/sync.ws.routes";
import { BaseElementTypes } from "@ehealth_tools/common/src/data/dto/base.dto";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";

export const WS_REFRESH_SERVICE = "WebSocketRefreshService";
type PushImpact<ElementTypes extends BaseElementTypes> = Map<keyof ElementTypes, Set<ID>>;
@Injectable(WS_REFRESH_SERVICE)
export class WebSocketRefreshService<ElementTypes extends BaseElementTypes, FetchDataTypes, FetchMethod extends keyof FetchDataTypes = keyof FetchDataTypes> {

    @TypedClientAjaxProxy()
    private readonly _dataRoutes!: DataRoutes<ElementTypes, ClientRouteOptions>;

    @Event()
    public refreshEvent!: EventHandler<SyncWSRoutes<ElementTypes>["REFRESH"], CommonEventInit>;

    /** Notify that something happen in the table concerning those DTO.ids */
    @Event()
    public pushEvent!: EventHandler<PushImpact<ElementTypes>, CommonEventInit>;

    /** Flag to know if the function `bindWebSocketEvents` has already ben called */
    private _alreadyBoundWebSocketEvents = false;

    /**
     * Bind some web sockets events to `ClientWebSocketService`.
     * It will listen to event `REFRESH`, `CREATE`, `UPDATE` and `DELETE`,
     * and you can register your custom events with the function `_bindCustomEvents`
     * @param fetchMethod Fetch method to invoke
     * @param fetchVariables Fetch variables for fetch method
     * @returns 
     */
    public bindWebSocketEvents(fetchMethod: FetchMethod, fetchVariables: FetchDataTypes[FetchMethod]): void {
        const log = getLogConsole();
        if (this._alreadyBoundWebSocketEvents) {
            log.warn("[WebSocketRefreshService]", "[bindWebSocketEvents]", "Function call when events are already bound");
            return;
        }

        try {

            const cacheService = injector.get<ClientActionCacheService<ElementTypes, FetchDataTypes>>(ACTION_CACHE_SERVICE);
            const webSocketService = injector.get<ClientWebSocketService>(CLIENT_WEB_SOCKET_SERVICE);

            webSocketService.on<SyncWSRoutes<ElementTypes>, "REFRESH">("REFRESH", (data) => {
                const toFetch: (keyof ElementTypes)[] = [];
                for (const table of objectKeys(data)) {
                    cacheService.getDTOCacheRepository(table).markDirty();
                    if (data[table]?.liveSync) {
                        toFetch.push(table);
                    }
                }
                if (toFetch.length) {
                    cacheService.fetchDataIfNeeded(fetchMethod, fetchVariables, toFetch).then(_ => {
                        this.refreshEvent.emit(data);
                    }).catch(CatchErrorWithDialog);
                } else {
                    this.refreshEvent.emit(data);
                }
            });

            webSocketService.on<SyncWSRoutes<ElementTypes>, "CREATE">("CREATE", (data) => {
                const impact: PushImpact<ElementTypes> = new Map();
                this._dataRoutes.getDataSecurely(data).then(freshDtosPerTable => {
                    for (const table of objectKeys(freshDtosPerTable)) {
                        for (const dto of freshDtosPerTable[table] ?? []) {
                            if (cacheService.getDTOCacheRepository(table).findDTOById(dto.id, { quiet: true }) == null) {
                                cacheService.getDTOCacheRepository(table).push(dto);
                                addToSetMap(impact, table, dto.id);
                            }
                        }
                    }
                    if (impact.size > 0) {
                        this.pushEvent.emit(impact);
                    }
                }).catch(e => getLogConsole().error(e));

            });

            webSocketService.on<SyncWSRoutes<ElementTypes>, "UPDATE">("UPDATE", (data) => {
                const impact: PushImpact<ElementTypes> = new Map();
                this._dataRoutes.getDataSecurely(data).then(freshDtosPerTable => {
                    for (const table of objectKeys(freshDtosPerTable)) {
                        for (const dto of freshDtosPerTable[table] ?? []) {
                            const localDTO = cacheService.getDTOCacheRepository(table).findDTOById(dto.id, { quiet: true });
                            if (localDTO) {
                                for (const prop of objectKeys(dto)) {
                                    setProp(localDTO, prop as string, dto[prop]);
                                }
                                addToSetMap(impact, table, localDTO.id);
                            }
                        }
                    }
                    if (impact.size > 0) {
                        this.pushEvent.emit(impact);
                    }
                }).catch(e => getLogConsole().error(e));

            });

            webSocketService.on<SyncWSRoutes<ElementTypes>, "DELETE">("DELETE", (data) => {
                const impact: PushImpact<ElementTypes> = new Map();
                for (const table of objectKeys(data)) {
                    for (const dtoId of data[table] ?? []) {
                        const localDTO = cacheService.getDTOCacheRepository(table).findDTOById(dtoId, { quiet: true });
                        if (localDTO) {
                            cacheService.getDTOCacheRepository(table).remove(localDTO);
                            addToSetMap(impact, table, localDTO.id);
                        } else {
                            getLogConsole().warn("A DTO delete pushed by the server is not existing locally... cache is unconsistent");
                        }
                    }
                }
                if (impact.size > 0) {
                    this.pushEvent.emit(impact);
                }
            });

            this._bindCustomEvents(webSocketService, cacheService);

        } catch (err) {
            log.error("[WebSocketRefreshService]", "[bindWebSocketEvents]", "Error when binding web socket events", err);
        } finally {
            this._alreadyBoundWebSocketEvents = true;
        }
    }

    /**
     * Function to bind custom web socket events
     * @param webSocketService `ClientWebSocketService` instance
     * @param cacheService `ClientActionCacheService` instance
     */
    protected _bindCustomEvents(webSocketService: ClientWebSocketService, cacheService: ClientActionCacheService<ElementTypes, FetchDataTypes>): void {
        // Do nothing. You can extend the class and override this method to bind custom web sockets events
    }
}